<script lang="ts" setup>
import { iconXSmall } from "@sit-onyx/icons";
import { useDensity } from "../../composables/density.js";
import { useVModel } from "../../composables/useVModel.js";
import { injectI18n } from "../../i18n/index.js";
import { useForwardProps } from "../../utils/props.js";
import OnyxBasicPopover from "../OnyxBasicPopover/OnyxBasicPopover.vue";
import OnyxHeadline from "../OnyxHeadline/OnyxHeadline.vue";
import OnyxSystemButton from "../OnyxSystemButton/OnyxSystemButton.vue";
import type { OnyxDialogProps } from "./types.js";

const props = withDefaults(defineProps<OnyxDialogProps>(), {
  open: undefined,
  sticky: true,
});

const emit = defineEmits<{
  /**
   * Emitted when the dialog should be closed.
   */
  "update:open": [open: boolean];
}>();

const slots = defineSlots<{
  /**
   * The trigger for the dialog. Should be an interactive component like a button or link.
   */
  trigger?(params: {
    /**
     * Attributes and event listeners that must be bound to an interactive element (button or link), that should act as the dialog trigger.
     */
    trigger: object;
  }): unknown;
  /**
   * Dialog content.
   */
  default(): unknown;
  /**
   * Optional slot to override the headline with custom content.
   * If unset, the `label` property will be shown.
   */
  headline?(bindings: Pick<OnyxDialogProps, "label">): unknown;
  /**
   * Optional footer slot to e.g. show action buttons (see OnyxBottomBar component).
   */
  footer?(): unknown;
}>();

const { t } = injectI18n();
const { densityClass } = useDensity(props);
const basicPopoverProps = useForwardProps(props, OnyxBasicPopover);

/**
 * If the dialog is expanded or not.
 */
const isExpanded = useVModel({
  props,
  emit,
  key: "open",
  default: false,
});
</script>

<template>
  <OnyxBasicPopover
    v-bind="basicPopoverProps"
    v-model:open="isExpanded"
    :class="['onyx-dialog', densityClass]"
  >
    <template #default="{ trigger }">
      <slot name="trigger" :trigger="trigger"> </slot>
    </template>
    <template #content>
      <div class="onyx-dialog__header">
        <div class="onyx-dialog__headline">
          <div class="onyx-dialog__headline-content">
            <slot name="headline" :label="props.label">
              <OnyxHeadline is="h2">{{ props.label }}</OnyxHeadline>
            </slot>
          </div>

          <OnyxSystemButton
            v-if="!props.nonDismissible"
            class="onyx-alert-dialog__close"
            :label="t('dialog.close')"
            :icon="iconXSmall"
            @click="() => (isExpanded = !isExpanded)"
          />
        </div>
      </div>

      <div class="onyx-dialog__body">
        <slot></slot>
      </div>

      <div v-if="!!slots.footer" class="onyx-dialog__footer">
        <slot name="footer"></slot>
      </div>
    </template>
  </OnyxBasicPopover>
</template>

<style lang="scss">
@use "../../styles/mixins/layers.scss";

.onyx-dialog {
  @include layers.component() {
    --onyx-dialog-padding-inline: var(--onyx-density-md);
    --onyx-basic-dialog-padding: 0;
    width: fit-content;
    .onyx-basic-popover__dialog {
      --onyx-basic-popover-max-width: calc(100% - 2 * (var(--onyx-density-md)));
    }

    .onyx-basic-dialog__content {
      display: flex;
      flex-direction: column;
    }

    &__header {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: var(--onyx-density-2xs);
      padding: var(--onyx-density-md) var(--onyx-dialog-padding-inline);
    }

    &__headline {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--onyx-density-sm);
    }

    &__headline-content {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: inherit;
      position: relative;
      width: 100%;
      overflow: hidden;
      justify-content: center;
    }

    &__body {
      overflow: auto;
      width: 100%;
      flex-grow: 1;
    }
    &__footer {
      width: 100%;
    }
  }
}
</style>
